'use client'

import React, { ReactElement, useContext, useEffect, useState } from 'react'
import { DropDownFooterProps } from '@/components/ui/wizard/DropDownFooter'
import classNames from 'classnames'
import { useDispatch } from 'react-redux'
import { useWizardSelector } from '@/components/ui/wizard/WizardState'
import DropDown from '@/components/ui/wizard/DropDown'
import { ConfigContext } from '@/components/common/ConfigContext'
import {
  setActiveStep,
  setFlexibleTravelPeriodSelected,
  setSelectedOptionCalendarFlexibility,
  setSelectedOptionDuration,
  toggleSelectedActionPeriods,
} from '@/components/ui/wizard/WizardActions'
import { onMobile, onMobileOrTablet } from '@/lib/utils/utils'
import { Duration, Month } from '@/types/Search'
import 'moment/locale/de'
import 'moment/locale/es'
import 'moment/locale/fr'
import 'moment/locale/it'
import 'moment/locale/nl'
import 'moment/locale/pl'
import 'moment/locale/sv'
import { useTranslations } from 'next-intl'
import DateRangePicker from '@/components/ui/DateRangePicker'

export const MONTHS = [
  'monthJanuary',
  'monthFebruary',
  'monthMarch',
  'monthApril',
  'monthMai',
  'monthJune',
  'monthJuli',
  'monthAugust',
  'monthSeptember',
  'monthOctober',
  'monthNovember',
  'monthDecember',
]

export const TRAVEL_PERIOD_MONTHS = MONTHS.map((m, i) => ({ month: i + 1, name: m }))
export const TRAVEL_PERIOD_OPTIONS = [...TRAVEL_PERIOD_MONTHS]

export const DATE_FLEXIBILITY_OPTIONS_CALENDAR = [
  { min: 0, max: 0, label: 'flexibilityExact', withoutPlusMinus: true },
  { min: 1, max: 1, label: 'flexibilityOneDay' },
  { min: 3, max: 3, label: 'flexibilityThreeDays' },
  { min: 7, max: 7, label: 'flexibilityOneWeek' },
]

export const DATE_FLEXIBILITY_OPTIONS_FLEXIBLE = [
  { min: 2, max: 3, label: 'flexibilityTwoToThreeDays' },
  { min: 4, max: 5, label: 'flexibilityFourToFiveDays' },
  { min: 7, max: 7, label: 'flexibilityOneWeek' },
  { min: 9, max: 12, label: 'flexibilityNineToTwelveDays' },
  { min: 14, max: 14, label: 'flexibilityTwoWeeks' },
  { min: 21, max: 21, label: 'flexibilityThreeWeeks' },
  { min: 28, max: 28, label: 'flexibilityFourWeeks' },
]

interface TravelPeriodProps extends DropDownFooterProps {
  search: () => void
  searchWizard: boolean | undefined
  absolutePositioned: boolean
  nextButtonActiveLandingPage: boolean
  withinModal?: boolean
  additionalClassName?: string
}

interface SwitchOptionOptions {
  label: string
  selected: boolean
  onClick: () => void
}

function SwitchOption(props: SwitchOptionOptions): ReactElement {
  const selectedStyles = 'bg-white p-1 rounded-md min-w-[50%] font-semibold'
  const unSelectedStyles = 'min-w-[50%] font-medium'
  const t = useTranslations('Wizard')

  return (
    <div
      className={classNames('inline-block cursor-pointer text-center text-stone-600', {
        [selectedStyles]: props.selected,
        [unSelectedStyles]: !props.selected,
      })}
      onClick={props.onClick}
    >
      {t(props.label)}
    </div>
  )
}

interface SwitchProps {
  searchWizard: boolean | undefined
}

function Switch(props: SwitchProps): ReactElement {
  const flexibleSelected = useWizardSelector((s) => s.flexibleTravelPeriodSelected)
  const dispatch = useDispatch()

  return (
    <div
      className={classNames('mx-auto mb-4 block w-full max-w-[340px] rounded-md bg-[#EDECEC] p-1', {
        'wm:mb-4 wm:mt-6': props.searchWizard,
        'wml:mb-4 wml:mt-6': !props.searchWizard,
      })}
    >
      <SwitchOption
        label="calendarTabLabel"
        selected={!flexibleSelected}
        onClick={() => dispatch(setFlexibleTravelPeriodSelected(false))}
      />
      <SwitchOption
        label={'flexibleTabLabel'}
        selected={!!flexibleSelected}
        onClick={() => dispatch(setFlexibleTravelPeriodSelected(true))}
      />
    </div>
  )
}

interface CalendarProps {
  withinModal: boolean
  searchWizard: boolean
}

function getOrientation(searchWizard: boolean): 'horizontal' | 'verticalScrollable' {
  const mobileView = searchWizard ? onMobile() : onMobileOrTablet()

  return mobileView ? 'verticalScrollable' : 'horizontal'
}

function Calendar(props: CalendarProps): ReactElement {
  const [orientation, setOrientation] = useState(getOrientation(props.searchWizard))

  useEffect(() => {
    function updateOrientation(): void {
      setOrientation(getOrientation(props.searchWizard))
    }

    window.addEventListener('resize', updateOrientation)

    return () => {
      window.removeEventListener('resize', updateOrientation)
    }
  }, [])

  return (
    <div
      className={classNames('no-tap-highlight no-scrollbar z-50 block', {
        'wd:h-auto wd:min-h-[310px] wm:bg-white': props.searchWizard,
        'wm:h-[calc(100%-60px)': props.withinModal,
        'h-full': !props.withinModal,
        'wdl:h-auto wdl:min-h-[310px] wml:bg-white': !props.searchWizard,
      })}
    >
      <DateRangePicker key={orientation} searchWizard={props.searchWizard} />
    </div>
  )
}

class TravelPeriodOptionProps<T> {
  option: T
  selected: boolean
  select: (option: T) => void
  searchWizard: boolean
  label: string
}

function TravelPeriodOption<T>(props: TravelPeriodOptionProps<T>): ReactElement {
  const t = useTranslations('Wizard')

  return (
    <div
      className={classNames(
        'mr-2 inline-block cursor-pointer rounded-full border-[1px] border-[#E0E0E0] px-4 font-medium text-primary',
        {
          'wd:mb-3 wd:py-1 wd:text-sm wm:mb-2.5 wm:py-2 wm:text-sm': props.searchWizard,
          'wdl:mb-3 wdl:py-1 wdl:text-sm wml:mb-2.5 wml:py-2 wml:text-sm': !props.searchWizard,
          'border-[1px] border-transparent bg-primary-lightened': props.selected,
        },
      )}
      onClick={() => props.select(props.option)}
    >
      {t(props.label)}
    </div>
  )
}

interface CalendarOptionProps {
  visible: boolean
  searchWizard: boolean
}

function Flexible(props: CalendarOptionProps): ReactElement {
  const selectedOptionPeriods = useWizardSelector<Month[] | undefined>(
    (s) => s.selectedOptionPeriods,
  )
  const selectedOptionDuration = useWizardSelector<Duration | undefined>(
    (s) => s.selectedOptionDuration,
  )
  const dispatch = useDispatch()
  const t = useTranslations('Wizard')

  function getCurrentMonthIndex(): number {
    return new Date().getMonth()
  }

  function sortedMonthList(): Month[] {
    const currentMonthIndex = getCurrentMonthIndex()
    const start = TRAVEL_PERIOD_MONTHS.slice(currentMonthIndex, 12)
    const end = TRAVEL_PERIOD_MONTHS.slice(0, currentMonthIndex)
    return [...start, ...end]
  }

  return (
    <div className="mb-10">
      <div
        className={classNames('block w-full text-center text-xl text-stone-700', {
          'wd:mb-5 wm:mb-4': props.searchWizard,
          'wdl:mb-5 wml:mb-4': !props.searchWizard,
          hidden: !props.visible,
        })}
      >
        {t('placeHolderSearchPageTravelPeriod')}
      </div>
      <div
        className={classNames(
          'flex flex-wrap justify-center wd:mx-auto wd:mb-2 wd:max-w-[500px] wd:text-center wm:mb-5 wm:justify-center wm:px-4 wm:text-center',
          {
            hidden: !props.visible,
          },
        )}
      >
        {sortedMonthList().map((o) => {
          return (
            <TravelPeriodOption
              key={o.month}
              option={o}
              selected={!!selectedOptionPeriods?.map((period) => period.month).includes(o.month)}
              select={(v) => dispatch(toggleSelectedActionPeriods(v))}
              searchWizard={props.searchWizard}
              label={o.name}
            />
          )
        })}
      </div>
      <div
        className={classNames('block w-full text-center text-xl text-stone-700 wd:mb-5 wm:mb-4', {
          hidden: !props.visible,
        })}
      >
        {t('travelPeriodDuration')}
      </div>
      <div
        className={classNames(
          'mb-5 flex flex-wrap justify-center text-center wd:mx-auto wd:max-w-[500px] wd:gap-3 wm:max-w-[100%] wm:gap-2.5 wm:px-4',
          {
            hidden: !props.visible,
          },
        )}
      >
        <FlexibilityOptions
          options={DATE_FLEXIBILITY_OPTIONS_FLEXIBLE}
          selectedOptionCalendarFlexibility={selectedOptionDuration}
          select={(v) => dispatch(setSelectedOptionDuration(v))}
          withPlusMinus={false}
        />
      </div>
    </div>
  )
}

function FlexibilityOptions(props: {
  withPlusMinus: boolean
  options: Duration[]
  selectedOptionCalendarFlexibility: Duration | undefined
  select: (d: Duration) => void
}): ReactElement {
  return (
    <>
      {props.options.map((o) => (
        <DropdownFooterOption
          key={o.label}
          duration={o}
          selected={props.selectedOptionCalendarFlexibility?.label === o.label}
          select={props.select}
          withPlusMinus={props.withPlusMinus && !o.withoutPlusMinus}
        />
      ))}
    </>
  )
}

export default function TravelPeriod(props: TravelPeriodProps): ReactElement {
  const flexibleSelected = useWizardSelector((s) => s.flexibleTravelPeriodSelected)
  const selectedOptionCalendarFlexibility = useWizardSelector(
    (s) => s.selectedOptionCalendarFlexibility,
  )
  const skipHeaderAndFooter = useContext(ConfigContext)!.skipHeaderAndFooter
  const dispatch = useDispatch()
  const mapLayoutABTestGroupB = true && !skipHeaderAndFooter

  function search(): void {
    dispatch(setActiveStep(-1))
    props.search()
  }

  function getSpecialNextAction(): (e?: React.MouseEvent<HTMLButtonElement>) => void {
    if (props.specialNextAction) {
      return props.specialNextAction
    }

    return props.searchWizard ? () => dispatch(setActiveStep(-1)) : search
  }

  return (
    <DropDown
      {...props}
      specialNextAction={getSpecialNextAction()}
      additionalClassName={classNames(
        'lg:max-w-[940px] md:min-w-[718px]',
        props.additionalClassName,
        {
          "lg:-translate-x-[472px] xl:-translate-x-[472px] wdl:h-['auto']":
            !props.searchWizard && !props.absolutePositioned,
          "md:-translate-x-[480px] lg:-translate-x-[458px] xl:-translate-x-[232px] md:max-w-[718px] wd:h-['auto']":
            props.searchWizard && !props.absolutePositioned && !mapLayoutABTestGroupB,
          "md:-translate-x-[480px] lg:translate-x-[-458px] xl:translate-x-[-360px] md:max-w-[718px] wd:h-['auto']":
            props.searchWizard && !props.absolutePositioned && mapLayoutABTestGroupB,
          'left-[-8px]': mapLayoutABTestGroupB,
          'left-[0px] top-[40px]': props.absolutePositioned,
        },
      )}
      primaryActionLabel={props.primaryActionLabel}
      footerOptions={
        !flexibleSelected && (
          <FlexibilityOptions
            options={DATE_FLEXIBILITY_OPTIONS_CALENDAR}
            selectedOptionCalendarFlexibility={
              selectedOptionCalendarFlexibility || DATE_FLEXIBILITY_OPTIONS_CALENDAR[0]
            }
            select={(o) =>
              o?.label !== selectedOptionCalendarFlexibility?.label &&
              dispatch(setSelectedOptionCalendarFlexibility(o))
            }
            withPlusMinus={true}
          />
        )
      }
    >
      <Switch searchWizard={props.searchWizard} />
      <div
        className={classNames('mx-auto h-full w-full', {
          'm:max-w-[100%] wd:mb-[10px] wd:max-h-[350px] wd:max-w-[650px] wm:overflow-y-auto':
            props.searchWizard,
          'wm:h-[calc(100%-300px)]': props.searchWizard && !flexibleSelected,
          'wm:h-[calc(100%-230px)]': props.searchWizard && flexibleSelected,
          'wdl:mb-[10px] wdl:max-h-[350px] wdl:max-w-[650px] wml:max-w-[100%] wml:overflow-y-auto':
            !props.searchWizard,
          'wml:h-[calc(100%-300px)]': !props.searchWizard && !flexibleSelected,
          'wml:h-[calc(100%-230px)]': !props.searchWizard && flexibleSelected,
        })}
      >
        {flexibleSelected ? (
          <Flexible visible={flexibleSelected} searchWizard={!!props.searchWizard} />
        ) : (
          <Calendar searchWizard={!!props.searchWizard} withinModal={!!props.withinModal} />
        )}
      </div>
    </DropDown>
  )
}

interface DropdownFooterOptionProps {
  withPlusMinus: boolean
  duration: Duration
  selected: boolean
  select: (o: Duration | undefined) => void
}

function DropdownFooterOption(props: DropdownFooterOptionProps): ReactElement {
  const t = useTranslations('Wizard')

  return (
    <div
      onClick={() => {
        props.select(props.selected ? undefined : props.duration)
      }}
      className={classNames(
        'h-[35px] cursor-pointer select-none items-center whitespace-nowrap rounded-full border-[1px] pr-2.5 text-md font-medium text-stone-900 wd:inline-flex wm:flex wm:flex-row',
        {
          'border-[#E0E0E0]': !props.selected,
          'border-transparent bg-primary-lightened': props.selected,
          'pl-2.5': !props.withPlusMinus,
          'pl-1.5': props.withPlusMinus,
        },
      )}
    >
      {props.withPlusMinus && <div className="plus-minus-tag" />}
      {t(props.duration.label)}
    </div>
  )
}
